import { useState, useRef, useCallback, useEffect } from 'react';
import { isPresignedUrl, generateCdnUrl } from '../utils/imageUtils';

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 1000;

export interface ImageRetryFailure {
  originalUrl: string;
  finalUrl: string;
  retryCount: number;
}

interface UseImageRetryOptions {
  src: string;
  maxRetries?: number;
  baseDelayMs?: number;
  onAllRetriesFailed?: (details: ImageRetryFailure) => void;
}

interface UseImageRetryResult {
  currentSrc: string;
  retryCount: number;
  hasExhaustedRetries: boolean;
  handleError: () => void;
  handleLoad: () => void;
}

export const useImageRetry = ({
  src,
  maxRetries = DEFAULT_MAX_RETRIES,
  baseDelayMs = DEFAULT_BASE_DELAY_MS,
  onAllRetriesFailed,
}: UseImageRetryOptions): UseImageRetryResult => {
  const [retryCount, setRetryCount] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasExhaustedRetries, setHasExhaustedRetries] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Reset state when src prop changes — synchronous setState in effect is intentional
  // here to keep retry state in sync with the src prop.
  useEffect(() => {
    setRetryCount(0); // eslint-disable-line react-hooks/set-state-in-effect
    setCurrentSrc(src);
    setHasExhaustedRetries(false);
  }, [src]);

  // Track mount state and cleanup timeout on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleError = useCallback(() => {
    const nextRetry = retryCount + 1;

    if (nextRetry > maxRetries) {
      setHasExhaustedRetries(true);
      onAllRetriesFailed?.({
        originalUrl: src,
        finalUrl: currentSrc,
        retryCount,
      });
      return;
    }

    const delay = baseDelayMs * Math.pow(2, nextRetry - 1);

    timeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;

      const isPresigned = isPresignedUrl(src);
      let nextSrc: string;

      if (!isPresigned && nextRetry >= 2) {
        // For CDN URLs, try jpeg fallback on retry 2+. Signed CDN URLs keep
        // their query string through generateCdnUrl, so pick the separator
        // accordingly — a second "?" would corrupt the signature params.
        const jpegUrl = generateCdnUrl(src, 'jpeg');
        nextSrc = `${jpegUrl}${jpegUrl.includes('?') ? '&' : '?'}retry=${nextRetry}`;
      } else {
        // Same URL with cache-bust param
        const separator = src.includes('?') ? '&' : '?';
        nextSrc = `${src}${separator}retry=${nextRetry}`;
      }

      setRetryCount(nextRetry);
      setCurrentSrc(nextSrc);
    }, delay);
  }, [retryCount, maxRetries, baseDelayMs, src, currentSrc, onAllRetriesFailed]);

  const handleLoad = useCallback(() => {
    setRetryCount(0);
    setHasExhaustedRetries(false);
  }, []);

  return { currentSrc, retryCount, hasExhaustedRetries, handleError, handleLoad };
};
